import { STORAGE_CONFIG } from '../../shared/constants/storage'

export interface IStorageService {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
  clear(): void
}

export class StorageService implements IStorageService {
  private usePersistentStorage: boolean = false

  constructor(usePersistentStorage: boolean = false) {
    this.usePersistentStorage = usePersistentStorage
  }

  setPersistentMode(isPersistent: boolean): void {
    this.usePersistentStorage = isPersistent
  }

  private getStorageType(key: string): Storage {
    // 설정에 따라 localStorage 또는 sessionStorage 선택
    if (STORAGE_CONFIG.PERSISTENT_KEYS.includes(key as any)) {
      return localStorage
    }
    // rememberMe가 활성화되어 있으면 토큰도 localStorage에 저장
    if (this.usePersistentStorage && STORAGE_CONFIG.SESSION_KEYS.includes(key as any)) {
      return localStorage
    }
    return sessionStorage
  }

  getItem(key: string): string | null {
    try {
      // 토큰 키는 localStorage와 sessionStorage 둘 다 확인
      if (STORAGE_CONFIG.SESSION_KEYS.includes(key as any)) {
        const sessionValue = sessionStorage.getItem(key)
        if (sessionValue) return sessionValue
        const localValue = localStorage.getItem(key)
        if (localValue) return localValue
        return null
      }

      const storage = this.getStorageType(key)
      return storage.getItem(key)
    } catch (error) {
      console.error(`Error getting item from storage: ${key}`, error)
      return null
    }
  }

  setItem(key: string, value: string): void {
    try {
      const storage = this.getStorageType(key)
      storage.setItem(key, value)
    } catch (error) {
      console.error(`Error setting item to storage: ${key}`, error)
    }
  }

  removeItem(key: string): void {
    try {
      // 토큰 키는 localStorage와 sessionStorage 둘 다에서 제거
      if (STORAGE_CONFIG.SESSION_KEYS.includes(key as any)) {
        sessionStorage.removeItem(key)
        localStorage.removeItem(key)
      } else {
        const storage = this.getStorageType(key)
        storage.removeItem(key)
      }
    } catch (error) {
      console.error(`Error removing item from storage: ${key}`, error)
    }
  }

  clear(): void {
    try {
      localStorage.clear()
      sessionStorage.clear()
    } catch (error) {
      console.error('Error clearing storage', error)
    }
  }

  // JSON 형태의 데이터를 위한 헬퍼 메서드
  getItemAsJson<T>(key: string): T | null {
    const item = this.getItem(key)
    if (!item) return null

    try {
      return JSON.parse(item)
    } catch (error) {
      console.error(`Error parsing JSON from storage: ${key}`, error)
      return null
    }
  }

  setItemAsJson<T>(key: string, value: T): void {
    try {
      const jsonString = JSON.stringify(value)
      this.setItem(key, jsonString)
    } catch (error) {
      console.error(`Error stringifying JSON to storage: ${key}`, error)
    }
  }
}
